import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment';
import { FC, useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { BasketDrawerVersion } from '@common/components/BasketDrawer';
import { ContentParser } from '@common/components/ContentParser';
import { Spinner } from '@common/components/Spinner';
import { Routes } from '@common/constants';
import { useLoadingState } from '@common/hooks';
import { JourneyPickerV2Connected } from '@features/JourneyPickerV2/container';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import {
  useGetSpaSettings,
  useGetSpaTreatment,
  useGetSpaTreatmentTimeslots,
  useGuestCountChange,
} from '@features/spa/hooks';
import { useTranslations } from '@lang';
import {
  getDiscountForMinimumGuests,
  getPriceForMinimumGuests,
} from '@utils/getPriceForMinimumGuests';
import { mergeCrossSellWithUpsell, parseDateToString } from '@utils/helpers';
import { mapDiscountToDisplayObject } from '@utils/mapDiscountToDisplayObject';
import {
  mapSpaCrossSellToRecommendations,
  SpaCrossSellEntry,
} from '@utils/mapSpaCrossSellToRecommendations';
import { useRouter } from 'shared-routing';

import { Recommendations, SpaTreatmentFacilityDetailTemplate } from 'voyager';

import { SpaTreatmentBookingV2 } from './SpaTreatmentBookingV2';
import { SpaTreatmentFormValues } from './SpaTreatmentBookingV2.types';
import { SpaTreatmentDetailV2Props } from './SpaTreatmentDetailV2.types';
import { SpaTreatmentExtrasV2 } from './SpaTreatmentExtrasV2';
import { spaEntityFormSchema } from './SpaTreatmentForm.schema';

export const SpaTreatmentDetailV2: FC<SpaTreatmentDetailV2Props> = ({
  id,
  bookingStep,
  property,
  language,
  journeyParams,
  loggedInGuest,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const [isBasketDrawerOpen, setIsBasketDrawerOpen] = useState(false);

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();

  const { data: spaEntity, isFetching } = useGetSpaTreatment(
    property.id,
    language,
    id,
    parseDateToString(journeyParams.spaDate)
  );

  const { isInitialLoading, isRefetching } = useLoadingState(isFetching, !!spaEntity);

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const termsAndConditions = spaSettings?.termsAndConditions || '';
  const hideArrivalTimes = spaSettings?.hideArrivalTimes || false;

  const selectedDateString = moment(journeyParams.spaDate).format('YYYY-MM-DD');

  const {
    data: timeslots,
    isFetching: isLoadingTimeslots,
    isError: isTimeslotsError,
  } = useGetSpaTreatmentTimeslots(
    property.id,
    id,
    selectedDateString,
    language,
    false,
    !!spaEntity,
    1
  );

  const isTelephoneOnly = spaEntity?.telephoneOnly || false;
  const isUnavailable =
    !isTelephoneOnly &&
    !isLoadingTimeslots &&
    (isTimeslotsError || (Array.isArray(timeslots) && timeslots.length === 0));

  const minGuests = spaEntity?.minimumGuests || 1;
  const maxGuests = spaEntity?.maximumGuests || minGuests;
  const requestedGuests = journeyParams.spaAdults || 1;
  const initialGuestCount = spaEntity
    ? Math.max(minGuests, Math.min(maxGuests, requestedGuests))
    : requestedGuests;

  const journeyBar = (
    <JourneyPickerV2Connected
      dateMode="single"
      dateContext="spa"
      showGuests
      maxGuests={spaEntity ? maxGuests : undefined}
      numberOfMonths={1}
      categories={navCategories}
      category="spa-treatments"
      onCategoryChange={handleNavCategoryChange}
      disableCategorySelect
    />
  );

  const guestLimitNotice =
    spaEntity && requestedGuests > maxGuests
      ? t('spa.maximumGuestsNotice', {
          spaEntity: t('spa.spaTreatment').toLowerCase(),
          maxGuests,
        })
      : undefined;

  const defaultGender =
    spaEntity?.genderSpecific === 'M' ? 'male' : spaEntity?.genderSpecific === 'F' ? 'female' : '';

  const form = useForm<SpaTreatmentFormValues>({
    resolver: zodResolver(spaEntityFormSchema),
    defaultValues: {
      date: moment(journeyParams.spaDate).format('YYYY-MM-DD'),
      guests: Array.from({ length: initialGuestCount }, () => ({
        title: '',
        firstName: '',
        lastName: '',
        gender: defaultGender,
      })),
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (form.getValues('date') !== selectedDateString) {
      form.setValue('date', selectedDateString, { shouldValidate: true });
    }
  }, [selectedDateString, form]);

  const handleGuestCountChange = useGuestCountChange({ form, defaultGender });

  useEffect(() => {
    if (!spaEntity) return;
    const target = Math.max(minGuests, Math.min(maxGuests, requestedGuests));
    if ((form.getValues('guests') || []).length !== target) {
      handleGuestCountChange(target);
    }
    if (defaultGender) {
      const guests = form.getValues('guests') || [];
      if (guests.some((g) => !g.gender)) {
        form.setValue(
          'guests',
          guests.map((g) => (g.gender ? g : { ...g, gender: defaultGender })),
          { shouldValidate: true, shouldDirty: true }
        );
      }
    }
  }, [
    spaEntity,
    minGuests,
    maxGuests,
    requestedGuests,
    defaultGender,
    form,
    handleGuestCountChange,
  ]);

  const handleBack = () => {
    const isCategoryView = spaSettings?.isTreatmentCategoryViewVisible;
    const categoryId = spaEntity?.categories?.[0]?.id;

    if (isCategoryView && categoryId) {
      router.push({
        pathname: Routes.SPA_TREATMENT_CATEGORY_DETAIL,
        query: { id: categoryId },
      });
    } else {
      router.push({
        pathname: Routes.SPA_TREATMENT_LIST,
        query: { arrivalDate: parseDateToString(journeyParams.spaDate) },
      });
    }
  };

  const handleReserve = () => {
    router.push({
      pathname: Routes.SPA_TREATMENT_BOOKING,
      query: { id, bookingStep: 'availability' },
    });
  };

  const handleCloseModal = useCallback(
    (showMiniBasket = false) => {
      router.push({ pathname: Routes.SPA_TREATMENT_DETAIL, query: { id } });
      if (showMiniBasket) {
        setIsBasketDrawerOpen(true);
      }
    },
    [router, id]
  );

  const crossSellItems = mergeCrossSellWithUpsell(
    spaEntity?.upSell,
    'spa-treatment',
    spaEntity?.crossSellObjects
  ) as SpaCrossSellEntry[];

  const { items: recommendations, targets: recommendationTargets } =
    mapSpaCrossSellToRecommendations(crossSellItems);

  const handleSelectRecommendation = (recId: string) => {
    const target = recommendationTargets.get(recId);
    if (target) {
      router.push({ pathname: target.route, query: { id: target.id } });
    }
  };

  const price = spaEntity
    ? getPriceForMinimumGuests(spaEntity.price, spaEntity.minimumGuests)
    : undefined;

  const discount = mapDiscountToDisplayObject(
    getDiscountForMinimumGuests(spaEntity?.discount, spaEntity?.minimumGuests ?? null)
  );

  const promotionName = spaEntity?.discount?.name;

  const durationLabel = spaEntity?.displayDuration ? `${spaEntity.displayDuration} min` : undefined;

  const propertyModules = Object.keys(property.modulesExcludingDisabled || {});

  return (
    <>
      {!bookingStep && (
        <>
          <Spinner showDelayMessage isActive={isRefetching} />
          <SpaTreatmentFacilityDetailTemplate
            imageUrl={spaEntity?.image?.url}
            imageAlt={spaEntity?.name}
            title={spaEntity?.name || ''}
            price={
              price?.amount != null
                ? { amount: price.amount, currencyCode: price.currencyCode }
                : undefined
            }
            discount={discount}
            priceLabel={t('common.from')}
            promotionName={promotionName}
            categoryLabel={durationLabel}
            description={spaEntity?.description}
            termsContent={
              spaEntity?.termsAndConditions || termsAndConditions ? (
                <ContentParser text={spaEntity?.termsAndConditions || termsAndConditions} />
              ) : undefined
            }
            termsLabel={t('app.termsAndConditions')}
            onReserve={
              spaEntity && !isTelephoneOnly && !isUnavailable && !isInitialLoading && !isRefetching
                ? handleReserve
                : undefined
            }
            reserveLabel={t('spa.book')}
            onBack={handleBack}
            isLoading={isInitialLoading}
            journeyBar={journeyBar}
            guestLimitNotice={guestLimitNotice}
            telephoneOnly={
              isTelephoneOnly
                ? {
                    title: t('spa.telephoneOnly', {
                      spaEntity: t('spa.spaTreatment').toLowerCase(),
                    }),
                    subtitle: t('treatment.callUsOn', {
                      phoneNumber: spaSettings?.phoneNumber,
                    }),
                  }
                : undefined
            }
            unavailable={
              isUnavailable
                ? {
                    title: t('common.unavailable'),
                    subtitle: t('spa.package.unavailableDescription', {
                      title: spaEntity?.name,
                      date: moment(selectedDateString).format('dddd Do MMMM YYYY'),
                    }),
                    ctaLabel: t('common.backToModuleRoot', { entity: t('app.spaTreatments') }),
                    onCtaClick: handleBack,
                  }
                : undefined
            }
            recommendations={
              recommendations.length > 0 ? (
                <Recommendations
                  items={recommendations}
                  onSelect={handleSelectRecommendation}
                  title={t('spa.headingColumnTitle')}
                  fromPriceLabel={t('common.from')}
                  noImageLabel={t('spa.noImage')}
                  visibleCount={{ mobile: 1.2, desktop: 3 }}
                  imageAspectRatio="1 / 1"
                />
              ) : undefined
            }
          />
        </>
      )}
      {bookingStep === 'availability' && spaEntity && (
        <FormProvider {...form}>
          <SpaTreatmentBookingV2
            id={id}
            property={property}
            language={language}
            onClose={handleCloseModal}
            loggedInGuest={loggedInGuest}
          />
        </FormProvider>
      )}
      {bookingStep === 'extras' && spaEntity && (
        <FormProvider {...form}>
          <SpaTreatmentExtrasV2
            spaEntity={spaEntity}
            property={property}
            language={language}
            id={id}
            onClose={handleCloseModal}
            onBack={() =>
              router.push({
                pathname: Routes.SPA_TREATMENT_BOOKING,
                query: { id, bookingStep: 'availability' },
              })
            }
          />
        </FormProvider>
      )}
      <BasketDrawerVersion
        open={isBasketDrawerOpen}
        onClose={() => setIsBasketDrawerOpen(false)}
        propertyModules={propertyModules}
        hideArrivalTimes={hideArrivalTimes}
      />
    </>
  );
};
